/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * useHtmlCssPreviewData — resolves html_css_workspace data bindings to real
 * table records for the admin's in-builder preview (HtmlCssWorkspaceModal),
 * so window.ARCEE_DATA is populated the same way it is on the published page
 * (see arcee-client's HtmlCssSection.js). Uses the session-authenticated
 * table-fetch endpoint (no public access key needed inside the admin app).
 */

import { useQuery, useQueries } from '@tanstack/react-query';
import { apiHelper } from './usePages';
import { sortAndLimitRecords, buildArceeData } from '../utils/htmlCssPreviewData';

export function useHtmlCssPreviewData(appId, bindings) {
  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ['tableList', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { tables: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  // GetTableRecordsAPIView hard-requires organization_id in the body (unlike
  // most other admin endpoints, which derive it server-side from the
  // session) — application/details/ is the only endpoint that hands the
  // client an org id, nested under `organization.id`.
  const { data: appDetails, isLoading: appDetailsLoading } = useQuery({
    queryKey: ['applicationDetails', appId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(`/arcee/application/details/${appId}/`, { method: 'GET', signal });
      if (!resp.ok) return null;
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const tables = tablesData?.tables || tablesData?.results || [];
  const organizationId = appDetails?.organization?.id;

  const bindingsWithTable = bindings.filter((b) => b.table_slug);

  const queries = useQueries({
    queries: bindingsWithTable.map((b) => ({
      queryKey: ['htmlCssPreviewRecords', appId, b.table_slug, b.limit, b.sort],
      queryFn: async () => {
        const table = tables.find((t) => (t.slug || t.name) === b.table_slug);
        if (!table) return [];
        const resp = await apiHelper.apiCall(
          '/arcee/application/table/fetch/?page_size=200',
          {
            method: 'POST',
            body: JSON.stringify({
              table_name: table.name,
              organization_id: organizationId,
              application_id: parseInt(appId, 10),
              filters: [],
            }),
          }
        );
        if (!resp.ok) return [];
        const json = await resp.json();
        return sortAndLimitRecords(json.records || [], b);
      },
      enabled: !tablesLoading && !appDetailsLoading && !!organizationId && !!appId,
      staleTime: 0,
    })),
  });

  const isLoading = tablesLoading || appDetailsLoading || queries.some((q) => q.isLoading);
  const data = buildArceeData(bindings, queries);

  return { data, isLoading };
}
